import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Tabs, type TabItem } from '@/components/common/Tabs';
import { PageHeader } from '@/components/common/PageHeader';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import { useToast } from '@/components/common/toast';
import { useAuth } from '@/auth/AuthContext';
import { usePermission } from '@/permissions/usePermission';
import { ApiError } from '@/api/client/abpError';
import { useChangePassword, useMyProfile, useUpdateMyProfile } from './api';
import { TwoFactorPanel } from './TwoFactorPanel';
import { ProfileDetailsTab } from './ProfileDetailsTab';
import { If1Tab } from './If1Tab';

type Tab = 'details' | 'personal' | 'security' | 'if1';

export function ProfilePage() {
  const { user, tenantId } = useAuth();
  const can = usePermission();
  const profileQuery = useMyProfile();

  const isTenant = !!tenantId;
  const canIf1 = can('User.Profile.IF1');

  const tabs = useMemo<TabItem[]>(
    () =>
      [
        isTenant && { key: 'details', title: 'Details' },
        { key: 'personal', title: 'Personal Information' },
        { key: 'security', title: 'Security' },
        isTenant && canIf1 && { key: 'if1', title: 'IF 01.00' },
      ].filter(Boolean) as TabItem[],
    [isTenant, canIf1],
  );
  const [tab, setTab] = useState<Tab>(isTenant ? 'details' : 'personal');

  if (profileQuery.isLoading) return <Spinner />;
  if (profileQuery.isError || !profileQuery.data)
    return <ErrorState message="Could not load your profile." onRetry={() => profileQuery.refetch()} />;

  const profile = profileQuery.data;
  const displayName = profile.userName || user?.userName || 'User';

  return (
    <div>
      <PageHeader title="Profile Information" breadcrumb={['Home', 'Profile']} />

      <div className="card custom-card mb-2">
        <div className="card-body">
          <div className="d-flex align-items-center flex-wrap">
            <span className="avatar avatar-xl avatar-rounded bg-light d-inline-flex align-items-center justify-content-center me-3">
              <i className="fa fa-user fs-1 text-muted" />
            </span>
            <div>
              <h4 className="mb-1">{displayName}</h4>
              <p className="text-muted mb-0">Email: {profile.email}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card custom-card mb-2">
        <div className="card-header p-0"><Tabs tabs={tabs} active={tab} onChange={(k) => setTab(k as Tab)} variant="underline" /></div>
        <div className="card-body">
          {tab === 'details' && isTenant && <ProfileDetailsTab tenantId={tenantId} />}
          {tab === 'personal' && <PersonalInfoTab profile={profile} />}
          {tab === 'security' && <SecurityTab hasPassword={profile.hasPassword} />}
          {tab === 'if1' && isTenant && canIf1 && <If1Tab />}
        </div>
      </div>
    </div>
  );
}

interface PersonalForm {
  userName: string;
  name: string;
  surname: string;
  email: string;
  phoneNumber: string;
}

function PersonalInfoTab({ profile }: { profile: { userName: string; email: string; name?: string | null; surname?: string | null; phoneNumber?: string | null; concurrencyStamp?: string | null } }) {
  const toast = useToast();
  const update = useUpdateMyProfile();
  const defaults = useMemo(
    () => ({ userName: profile.userName, name: profile.name ?? '', surname: profile.surname ?? '', email: profile.email, phoneNumber: profile.phoneNumber ?? '' }),
    [profile],
  );
  const { register, handleSubmit, reset } = useForm<PersonalForm>({ defaultValues: defaults });
  useEffect(() => reset(defaults), [defaults, reset]);

  function onSubmit(values: PersonalForm) {
    update.mutate(
      { ...values, concurrencyStamp: profile.concurrencyStamp },
      {
        onSuccess: () => toast.success('Profile updated.'),
        onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed to update profile.'),
      },
    );
  }

  return (
    // Two-column, full-width row — matches the legacy Profile → Personal Information
    // form (col-md-6 inputs side by side, no width cap), under its section heading.
    <form onSubmit={handleSubmit(onSubmit)} className="row g-3">
      <h4 className="col-12 fw-semibold mb-0">Personal Information</h4>
      <div className="col-md-6"><label className="form-label">User Name</label><input className="form-control" maxLength={256} {...register('userName')} /></div>
      <div className="col-md-6"><label className="form-label">Email</label><input type="email" className="form-control" maxLength={256} {...register('email')} /></div>
      <div className="col-md-6"><label className="form-label">Name</label><input className="form-control" maxLength={64} {...register('name')} /></div>
      <div className="col-md-6"><label className="form-label">Surname</label><input className="form-control" maxLength={64} {...register('surname')} /></div>
      <div className="col-md-6"><label className="form-label">Phone Number</label><input type="tel" className="form-control" maxLength={32} {...register('phoneNumber')} /></div>
      <div className="col-12 text-end">
        <button type="submit" className="btn btn-primary" disabled={update.isPending}>
          {update.isPending ? <i className="fa fa-spinner fa-spin me-1" /> : null}Save
        </button>
      </div>
    </form>
  );
}

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

function PwdField({ label, autoComplete, register, invalid }: { label: string; autoComplete: string; register: object; invalid?: boolean }) {
  const [show, setShow] = useState(false);
  return (
    <div className="mb-3">
      <label className="form-label">{label}</label>
      <div className="input-group">
        <input type={show ? 'text' : 'password'} className={`form-control${invalid ? ' is-invalid' : ''}`} autoComplete={autoComplete} {...register} />
        <button type="button" className="btn btn-light" tabIndex={-1} title={show ? 'Hide' : 'Show'} onClick={() => setShow((v) => !v)}>
          <i className={`fa ${show ? 'fa-eye-slash' : 'fa-eye'}`} />
        </button>
      </div>
    </div>
  );
}

function SecurityTab({ hasPassword }: { hasPassword: boolean }) {
  const toast = useToast();
  const change = useChangePassword();
  const { register, handleSubmit, reset, formState: { errors } } = useForm<PasswordForm>();

  function onSubmit(values: PasswordForm) {
    if (values.newPassword !== values.confirmPassword) {
      toast.error('New password and confirmation do not match.');
      return;
    }
    change.mutate(
      { currentPassword: hasPassword ? values.currentPassword : undefined, newPassword: values.newPassword },
      {
        onSuccess: () => { toast.success('Password changed.'); reset(); },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed to change password.'),
      },
    );
  }

  return (
    <div className="row g-4">
      <div className="col-lg-6">
        <h6 className="fw-semibold mb-3">{hasPassword ? 'Change Password' : 'Set Password'}</h6>
        <form onSubmit={handleSubmit(onSubmit)} style={{ maxWidth: 480 }} autoComplete="off">
          {hasPassword && <PwdField label="Current Password" autoComplete="current-password" invalid={!!errors.currentPassword} register={register('currentPassword', { required: hasPassword })} />}
          <PwdField label="New Password" autoComplete="new-password" invalid={!!errors.newPassword} register={register('newPassword', { required: true })} />
          <PwdField label="Confirm New Password" autoComplete="new-password" invalid={!!errors.confirmPassword} register={register('confirmPassword', { required: true })} />
          <button type="submit" className="btn btn-primary" disabled={change.isPending}>
            {change.isPending ? <i className="fa fa-spinner fa-spin me-1" /> : null}{hasPassword ? 'Change Password' : 'Set Password'}
          </button>
        </form>
      </div>
      <div className="col-lg-6 border-start">
        <TwoFactorPanel />
      </div>
    </div>
  );
}

